import React from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="white" stroke="none" />
    </svg>
  ),
  twitter: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53A4.48 4.48 0 0 0 22.43.36a9 9 0 0 1-2.88 1.1A4.52 4.52 0 0 0 16.11 0c-2.5 0-4.52 2.02-4.52 4.52 0 .35.04.7.11 1.03C7.69 5.4 4.07 3.6 1.64.96a4.52 4.52 0 0 0-.61 2.27c0 1.57.8 2.95 2.01 3.76a4.5 4.5 0 0 1-2.05-.57v.06c0 2.19 1.56 4.02 3.63 4.43a4.55 4.55 0 0 1-2.04.08 4.53 4.53 0 0 0 4.22 3.14A9.07 9.07 0 0 1 0 19.54a12.8 12.8 0 0 0 6.92 2.03c8.3 0 12.85-6.88 12.85-12.85 0-.2 0-.39-.01-.58A9.17 9.17 0 0 0 23 3z" />
    </svg>
  ),
  youtube: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
    </svg>
  ),
  whatsapp: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.213a.75.75 0 0 0 .928.928l5.389-1.466A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.523-5.188-1.432l-.372-.222-3.853 1.048 1.048-3.853-.222-.372A9.956 9.956 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  ),
  email: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polyline points="2,4 12,13 22,4" />
    </svg>
  ),
};

const Footer: React.FC = () => {
  const org = useOrg();
  const orgName = org?.org_name || 'Our Store';
  const year = new Date().getFullYear();

  const addressLines = [
    org?.address_line_1 || '',
    [org?.address_line_2, org?.city].filter(Boolean).join(', '),
    [org?.state, org?.pincode].filter(Boolean).join(' - '),
  ].filter(Boolean);

  const socials = org?.social_media_url?.filter((s) => !!s.url) ?? [];
  const emailInSocials = socials.some((s) => s.type === 'email');
  const emailEntry = !emailInSocials && org?.email_id
    ? { type: 'email', url: org.email_id }
    : null;

  return (
    <footer className="footer">
      <div className="footer__inner">
        {/* Brand */}
        <div className="footer__brand">
          <div className="footer__logo">
            {(org?.ecommerce_logo || org?.image) && (
              <img
                src={org.ecommerce_logo || org.image!}
                alt={orgName}
                className="footer__logo-img"
              />
            )}
            <span className="footer__logo-main">{orgName}</span>
            {org?.description?.motto && (
              <strong className="footer__logo-sub">
                {org.description.motto?.toUpperCase()}
              </strong>
            )}
          </div>
          {org?.description?.short_description && (
            <p className="footer__tagline">{org.description.short_description}</p>
          )}
          {addressLines.length > 0 && (
            <p className="footer__address">
              {addressLines.map((line, i) => (
                <span key={i}>{line}<br /></span>
              ))}
            </p>
          )}
          {(socials.length > 0 || emailEntry) && (
            <div className="footer__socials">
              {socials.map((s) => (
                <a
                  key={s.type}
                  href={s.type === 'email' ? `mailto:${s.url}` : s.url}
                  className="footer__social"
                  aria-label={s.type}
                  target={s.type === 'email' ? undefined : '_blank'}
                  rel="noopener noreferrer"
                >
                  {SOCIAL_ICONS[s.type.toLowerCase()] ?? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  )}
                </a>
              ))}
              {emailEntry && (
                <a href={`mailto:${emailEntry.url}`} className="footer__social" aria-label="email">
                  {SOCIAL_ICONS['email']}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Company links */}
        <div className="footer__col">
          <h4>Company</h4>
          <Link to="/">About Us</Link>
          <Link to="/contact">Contact Us</Link>
        </div>

        {/* Shop links */}
        <div className="footer__col">
          <h4>Shop</h4>
          <Link to="/products?sort=newest">New Arrivals</Link>
          <Link to="/products?ideal_for=1">Men</Link>
          <Link to="/products?ideal_for=2">Women</Link>
          <Link to="/products?ideal_for=3">Kids</Link>
          <Link to="/products">All Products</Link>
        </div>
      </div>

      <div className="footer__bottom">
        <p>© {year} {orgName}. All rights reserved.</p>
        <p className="footer__clustrex">Powered by <a href="https://korv.ai/optikorv.html" target="_blank" rel="noopener noreferrer">OptiKorv</a></p>
      </div>
    </footer>
  );
};

export default Footer;
